import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as CloseIcon } from "../../assets/images/x.svg";
import { ReactComponent as Dropdown } from "../../assets/images/dropdown-arrow.svg";
import {
  peopleSelectControlSx,
  peopleSelectMenuProps,
  peopleSelectSx,
} from "../dashboardcomponents/People/peopleSelectStyles";
import { dangerButtonSx, labelSx, primaryButtonSx, secondaryButtonSx } from "./platformUi";

const TONES = {
  danger: { bg: "#FFF1F0", color: "#FF4D4F", Icon: WarningAmberRoundedIcon },
  warning: { bg: "#FFF7E6", color: "#FA8C16", Icon: WarningAmberRoundedIcon },
  info: { bg: "#E6F4FF", color: "#148DFD", Icon: InfoOutlinedIcon },
};

const textInputSx = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #D5DCE1",
  borderRadius: "8px",
  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
  fontFamily: "Inter",
  fontSize: 14,
  color: "#061522",
  outline: "none",
  "&:focus": { borderColor: "#148DFD" },
  "&::placeholder": { color: "#93A1B0" },
};

/**
 * config: {
 *   tone, title, description,
 *   select?: { label, options: [{ value, label }], defaultValue },
 *   note?: { label, placeholder, required },
 *   confirmText?: string  // user must type this exactly to enable confirm
 *   confirmLabel
 * }
 */
export default function PlatformActionModal({ open, config, onClose, onConfirm }) {
  const [selectValue, setSelectValue] = useState("");
  const [note, setNote] = useState("");
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open || !config) return;
    setSelectValue(config.select?.defaultValue ?? config.select?.options?.[0]?.value ?? "");
    setNote("");
    setTyped("");
  }, [open, config]);

  if (!config) return null;

  const tone = TONES[config.tone] || TONES.info;
  const ToneIcon = tone.Icon;
  const noteMissing = config.note?.required && !note.trim();
  const typedMismatch = config.confirmText && typed.trim() !== config.confirmText;
  const disabled = Boolean(noteMissing || typedMismatch);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(255, 255, 255, 0.6)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
        },
      }}
      PaperProps={{
        sx: {
          width: 440,
          maxWidth: "95vw",
          borderRadius: "12px",
          border: "1px solid #D5DCE1",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          m: 2,
        },
      }}
    >
      <Box sx={{ p: "24px", position: "relative" }}>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", top: 16, right: 16, p: 0.5 }}>
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: tone.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <ToneIcon sx={{ color: tone.color, fontSize: 22 }} />
        </Box>

        <Typography sx={{ color: "#061522", fontFamily: "Inter", fontSize: 18, fontWeight: 500, lineHeight: "20px", mb: 1 }}>
          {config.title}
        </Typography>
        <Typography sx={{ color: "#556575", fontFamily: "Inter", fontSize: 14, lineHeight: "22px", mb: 2.5 }}>
          {config.description}
        </Typography>

        {config.select && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.75 }}>{config.select.label}</Typography>
            <FormControl fullWidth size="small" sx={peopleSelectControlSx({ width: "100%" })}>
              <Select
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                IconComponent={Dropdown}
                MenuProps={peopleSelectMenuProps}
                sx={peopleSelectSx}
              >
                {config.select.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {config.note && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.75 }}>
              {config.note.label}
              {config.note.required ? <Box component="span" sx={{ color: "#FF4D4F" }}> *</Box> : null}
            </Typography>
            <Box
              component="textarea"
              rows={3}
              value={note}
              placeholder={config.note.placeholder}
              onChange={(e) => setNote(e.target.value)}
              sx={{ ...textInputSx, p: "10px 14px", resize: "vertical", minHeight: 80 }}
            />
          </Box>
        )}

        {config.confirmText && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...labelSx, mb: 0.75 }}>
              Type <Box component="span" sx={{ color: "#061522", fontWeight: 500 }}>{config.confirmText}</Box> to confirm
            </Typography>
            <Box
              component="input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              sx={{ ...textInputSx, height: 40, px: "14px" }}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button onClick={onClose} sx={{ ...secondaryButtonSx, flex: 1 }}>
            Cancel
          </Button>
          <Button
            disabled={disabled}
            onClick={() => onConfirm({ selectValue, note: note.trim() })}
            sx={{
              ...(config.tone === "danger" ? dangerButtonSx : primaryButtonSx),
              flex: 1,
              "&.Mui-disabled": { background: "#F9FAFA", color: "#93A1B0", border: "1px solid #D5DCE1" },
            }}
          >
            {config.confirmLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
